// src/pages/Archive/index.js
import React, { useEffect, useMemo, useState } from "react";
import { Card, Typography, Space, Button } from "antd";
import dayjs from "dayjs";
import ArchiveList from "../../components/Archive/index";
import { listPosts } from "../../api/mockData";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

export default function Archive() {
  const [posts, setPosts] = useState([]);
  const [monthOffset, setMonthOffset] = useState(0);
  const [slideDir, setSlideDir] = useState("");

  useEffect(() => {
    listPosts().then(setPosts);
  }, []);

  const activityMap = useMemo(() => {
    const map = new Map();
    posts.forEach((p) => {
      const key = dayjs(p.createdAt).format("YYYY-MM-DD");
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [posts]);

  const activeMonth = useMemo(
    () => dayjs().startOf("month").add(monthOffset, "month"),
    [monthOffset]
  );

  const months = useMemo(() => {
    return [-2, -1, 0, 1, 2].map((delta) =>
      activeMonth.add(delta, "month").startOf("month")
    );
  }, [activeMonth]);

  const getMonthDays = (month) => {
    const days = month.daysInMonth();
    return Array.from({ length: days }, (_, idx) => month.add(idx, "day"));
  };

  const monthlyStats = useMemo(() => {
    const map = new Map();
    posts.forEach((p) => {
      const key = dayjs(p.createdAt).format("YYYY-MM");
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, count]) => ({ month, count }));
  }, [posts]);

  const recentMonthly = useMemo(
    () => (monthlyStats.length > 8 ? monthlyStats.slice(-8) : monthlyStats),
    [monthlyStats]
  );

  const tagStats = useMemo(() => {
    const map = new Map();
    posts.forEach((p) => {
      (p.tags || []).forEach((t) => {
        map.set(t, (map.get(t) || 0) + 1);
      });
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));
  }, [posts]);

  const categoryStats = useMemo(() => {
    const map = new Map();
    posts.forEach((p) => {
      const key = p.category || "未分类";
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));
  }, [posts]);

  const pieColors = ["#60a5fa", "#34d399", "#fbbf24", "#f87171", "#a78bfa", "#22d3ee"];

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Card className="themed-card" bodyStyle={{ padding: "18px 22px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Button
            onClick={() => {
              setSlideDir("left");
              setMonthOffset((v) => v - 1);
            }}
          >
            上一月
          </Button>
          <div style={{ flex: 1, textAlign: "center" }}>
            <Typography.Text
              style={{
                color: "var(--text-primary)",
                fontWeight: 600,
                fontSize: 16,
              }}
            >
              {activeMonth.format("YYYY年 M 月")}
            </Typography.Text>
          </div>
          <Button
            onClick={() => {
              setSlideDir("right");
              setMonthOffset((v) => v + 1);
            }}
          >
            下一月
          </Button>
        </div>

        <div
          className={`contrib-row ${slideDir ? `slide-${slideDir}` : ""}`}
          style={{ marginTop: 14 }}
          onAnimationEnd={() => setSlideDir("")}
        >
          {months.map((month) => {
            const days = getMonthDays(month);
            return (
              <div
                key={month.format("YYYY-MM")}
                className={`contrib-month ${
                  month.isSame(activeMonth, "month") ? "contrib-month-current" : ""
                }`}
              >
                <Typography.Text
                  style={{
                    display: "block",
                    marginBottom: 8,
                    color: "var(--text-secondary)",
                    fontSize: 12,
                    textAlign: "center",
                  }}
                >
                  {month.format("M月")}
                </Typography.Text>
                <div className="contrib-grid">
                  {days.map((day) => {
                    const count = activityMap.get(day.format("YYYY-MM-DD")) || 0;
                    const level =
                      count >= 3 ? 3 : count >= 2 ? 2 : count >= 1 ? 1 : 0;
                    return (
                      <div
                        key={day.format("YYYY-MM-DD")}
                        className={`contrib-cell contrib-${level}`}
                        title={`${day.format("MM-DD")} · ${count} 篇`}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="themed-card" bodyStyle={{ padding: "22px 26px" }}>
        <Typography.Title level={3} style={{ color: "var(--text-primary)" }}>
          数据统计
        </Typography.Title>
        <div className="archive-charts">
          <div className="archive-chart">
            <Typography.Text style={{ color: "var(--text-secondary)" }}>
              月度发布趋势
            </Typography.Text>
            <div className="archive-chart-body">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={recentMonthly}>
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#60a5fa" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="archive-chart">
            <Typography.Text style={{ color: "var(--text-secondary)" }}>
              标签分布
            </Typography.Text>
            <div className="archive-chart-body">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={tagStats} dataKey="value" nameKey="name" innerRadius={45}>
                    {tagStats.map((entry, idx) => (
                      <Cell key={entry.name} fill={pieColors[idx % pieColors.length]} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="archive-chart">
            <Typography.Text style={{ color: "var(--text-secondary)" }}>
              分类分布
            </Typography.Text>
            <div className="archive-chart-body">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={categoryStats}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={45}
                  >
                    {categoryStats.map((entry, idx) => (
                      <Cell key={entry.name} fill={pieColors[idx % pieColors.length]} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </Card>

      <Card className="themed-card" bodyStyle={{ padding: "22px 26px" }}>
        <Typography.Title level={3} style={{ color: "var(--text-primary)" }}>
          归档
        </Typography.Title>
        <ArchiveList posts={posts} />
      </Card>
    </Space>
  );
}
